import React from "react";
import { Mail, Phone } from "lucide-react";
import { apiClient } from "../lib/apiClient";

export function ContactsSection({ id, lang }) {
  const [contacts, setContacts] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await apiClient.get("/public/contacts");
        setContacts(response.data);
      } catch (err) {
        console.error("Failed to fetch contacts:", err);
        // Fallback to default
        setContacts({
          washington: {
            name: "Washington, D.C. Office",
            address: "601 Pennsylvania Avenue, Suite 900, Washington, DC 20004",
            description_en: "Our Washington office is in the federal district near major agencies, policy groups, and congressional offices.",
            description_uk: "Наш офіс у Вашингтоні розташований у федеральному окрузі поблизу великих агенцій.",
            phone: "+1 (202) 322-7644",
            email: "info@strategiclobbyistalliance.com",
            image_url: "/assets/washington-office.jpg"
          },
          kyiv: {
            name: "Kyiv Office",
            address: "2, Khrestoyi Alley, 5th Floor, Kyiv, 01010, Ukraine",
            description_en: "Our Kyiv office is in the diplomatic and government district.",
            description_uk: "Наш офіс у Києві розташований у дипломатичному районі.",
            phone: "+38 (077) 777 02 59",
            email: "info@sla-lobbying.com",
            image_url: "/assets/kyiv-office.jpg"
          }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, []);

  if (loading || !contacts) {
    return <section id={id} className="bg-slate-50 py-16 sm:py-20"><div className="mx-auto max-w-6xl px-4 md:px-6 text-center">Loading...</div></section>;
  }

  const offices = [
    {
      ...contacts.kyiv,
      description: lang === "en" ? contacts.kyiv.description_en : contacts.kyiv.description_uk,
      image: contacts.kyiv.image_url
    },
    {
      ...contacts.washington,
      description: lang === "en" ? contacts.washington.description_en : contacts.washington.description_uk,
      image: contacts.washington.image_url
    }
  ];

  return (
    <section id={id} className="bg-slate-50 py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="mb-12 space-y-3">
          <p className="text-base font-semibold uppercase tracking-[0.2em] text-amber-700">
            {lang === "en" ? "Contacts" : "Контакти"}
          </p>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            {lang === "en"
              ? "Let us design your engagement strategy"
              : "Створімо вашу стратегію взаємодії"}
          </h2>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {offices.map((office, idx) => (
            <div key={idx} className="overflow-hidden rounded-2xl bg-white shadow-xl">
              {/* Office Image */}
              <div className="h-64 overflow-hidden">
                <img 
                  src={office.image} 
                  alt={office.name}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Office Info */}
              <div className="p-8 space-y-6">
                <div>
                  <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">
                    {office.name}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {office.address}
                  </p>
                </div>

                <p className="text-sm leading-relaxed text-slate-700">
                  {office.description}
                </p>

                <div className="space-y-3 border-t border-slate-200 pt-6">
                  {office.phone && (
                  <a 
                    href={`tel:${office.phone.replace(/\s/g, '')}`}
                    className="flex items-center gap-3 text-sm text-slate-900 hover:text-amber-600 transition-colors group"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 group-hover:bg-amber-100 transition-colors">
                      <Phone className="h-5 w-5 text-slate-600 group-hover:text-amber-600" />
                    </div>
                    <span className="font-medium">{office.phone}</span>
                  </a>
                  )}

                  {office.email && (
                  <a 
                    href={`mailto:${office.email}`}
                    className="flex items-center gap-3 text-sm text-slate-900 hover:text-amber-600 transition-colors group"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 group-hover:bg-amber-100 transition-colors">
                      <Mail className="h-5 w-5 text-slate-600 group-hover:text-amber-600" />
                    </div>
                    <span className="font-medium">{office.email}</span>
                  </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
